// เรทค่าเงินกลาง (บาทต่อ 1 หน่วย) — ตาราง public.fx_defaults
// Purim 1 ต.ค. 2026: "ควรใส่ค่ากลางไว้ 1 ค่า ที่แก้ไขได้ เช่น เยน 0.22 · USD 36"
// ใช้เติมเรทให้ใบ PI / ใบสั่งซื้อที่ยังว่าง (เดิมว่าง = ต้นทุนไม่ไหลเข้า PD และมูลค่าใบเป็น ฿0)
// อ่านได้ทุกคนที่ล็อกอิน · แก้ได้เฉพาะ role ต้นทุน (RLS: has_cost_access)
import { useEffect, useState } from 'react';
import { supabase } from './supabase.js';

let cache = null;          // { JPY: 0.22, USD: 36, ... }
let inflight = null;
const subs = new Set();

export async function loadFxDefaults(force = false) {
  if (cache && !force) return cache;
  if (inflight && !force) return inflight;
  inflight = (async () => {
    const { data, error } = await supabase.from('fx_defaults').select('currency, rate, updated_at, updated_by');
    if (error) throw error;
    const next = {};
    const meta = {};
    for (const r of data ?? []) {
      next[r.currency] = Number(r.rate);
      meta[r.currency] = { updated_at: r.updated_at, updated_by: r.updated_by };
    }
    cache = next;
    cache.__meta = meta;
    subs.forEach(fn => fn(cache));
    return cache;
  })();
  try { return await inflight; } finally { inflight = null; }
}

// เรทกลางของสกุลนั้น (THB = 1 เสมอ) · ไม่มี = null
export function fxDefaultOf(currency, table = cache) {
  const c = String(currency || '').toUpperCase();
  if (!c) return null;
  if (c === 'THB') return 1;
  const v = table?.[c];
  return v > 0 ? v : null;
}

export async function saveFxDefault(currency, rate, who) {
  const c = String(currency || '').toUpperCase().trim();
  const r = Number(rate);
  if (!/^[A-Z]{3}$/.test(c)) throw new Error('รหัสสกุลเงินต้องเป็นตัวอักษร 3 ตัว เช่น JPY');
  if (c === 'THB') throw new Error('THB ไม่ต้องตั้งเรท (= 1 เสมอ)');
  if (!(r > 0)) throw new Error('เรทต้องมากกว่า 0');
  const { error } = await supabase.from('fx_defaults')
    .upsert({ currency: c, rate: r, updated_at: new Date().toISOString(), updated_by: who || null });
  if (error) throw new Error(error.message.includes('row-level security')
    ? 'บัญชีนี้แก้เรทกลางไม่ได้ (เฉพาะ role จัดซื้อ / ผู้จัดการ / บัญชี)'
    : error.message);
  return loadFxDefaults(true);
}

// hook: ได้ตารางเรทกลาง + อัปเดตเองเมื่อมีคนแก้ในหน้าเดียวกัน
export function useFxDefaults() {
  const [table, setTable] = useState(cache);
  useEffect(() => {
    subs.add(setTable);
    loadFxDefaults().then(setTable).catch(e => console.warn('โหลดเรทกลางไม่สำเร็จ:', e?.message ?? e));
    return () => { subs.delete(setTable); };
  }, []);
  return table;
}

// เรทที่ผิดปกติสำหรับสกุลนั้น (ช่วงเดียวกับ by Photos) — กันพิมพ์เรท USD ลงใบเยน ฯลฯ · ไม่บล็อก แค่เตือน
const RATE_RANGE = { JPY: [0.15, 0.35], USD: [25, 42], CNY: [3.5, 6.5], EUR: [32, 46], GBP: [38, 54] };
export function fxRangeWarn(currency, rate) {
  const c = String(currency || '').toUpperCase();
  const r = Number(rate);
  const rg = RATE_RANGE[c];
  if (!rg || !(r > 0) || (r >= rg[0] && r <= rg[1])) return '';
  return `เรท ${r} ผิดปกติสำหรับ ${c} (ปกติ ${rg[0]}–${rg[1]}) — ตรวจอีกครั้ง`;
}
